import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { notification, message } from "antd";
import { MakeApiCall } from "../apis";
import { useHubSpotSync } from "./useHubSpotSync";

/**
 * Triggers the server-side products/export job.
 * On success shows a dismissible notification that guides the user to
 * the Upload Manager where the generated file will appear.
 *
 * Usage:
 *   const { downloadTemplate, templateLoading } = useProductTemplateExport(userData);
 */
export const useProductTemplateExport = (userData) => {
    const [templateLoading, setTemplateLoading] = useState(false);
    const navigate = useNavigate();
    const { syncActionTimestamp } = useHubSpotSync(userData);

    const downloadTemplate = async () => {
        if (templateLoading) return;
        syncActionTimestamp("sv_used_import_export");
        setTemplateLoading(true);

        try {
            const response = await MakeApiCall(
                "products/export",
                "post",
                null,
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                const fileId = response.data?.file_id ?? response.data?.export_file_id ?? "";
                const key = `export-notif-${Date.now()}`;
                notification.success({
                    key,
                    message: "Export started",
                    description: (
                        <span>
                            {fileId && <><b>{fileId}</b> — </>}
                            {response.message ?? "Your file is being prepared."}{" "}
                            <a
                                href="/settings/file-manager?tab=export"
                                style={{ color: "#f45b68", fontWeight: 600 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    notification.destroy(key);
                                    navigate("/settings/file-manager?tab=export");
                                }}
                            >
                                Go to Import & Export
                            </a>
                        </span>
                    ),
                    duration: 0,
                    placement: "top",
                    style: { width: "max-content" }
                });
            } else {
                message.error(response?.message || "Export failed. Please try again.");
            }
        } catch (err) {
            console.error("Products export error:", err);
            message.error("Something went wrong while starting the export.");
        } finally {
            setTemplateLoading(false);
        }
    };

    return { downloadTemplate, templateLoading };
};